import { useSyncExternalStore } from 'react'

export interface IAtlasPreviewAnchor {
  element: HTMLElement | null
  /** A click pins the card; a hover only shows it while the pointer stays on chip or card. */
  isPinned: boolean
}

/** Long enough to cross the gap between the chip and the card without it closing. */
export const PREVIEW_HIDE_DELAY_MS = 200

const NO_ANCHOR: IAtlasPreviewAnchor = { element: null, isPinned: false }

let current = NO_ANCHOR
let hideTimer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

const publish = (next: IAtlasPreviewAnchor) => {
  current = next
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getPreviewAnchor = (): IAtlasPreviewAnchor => current

export const cancelHidePreview = () => {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

export const anchorPreview = (element: HTMLElement, isPinned: boolean) => {
  cancelHidePreview()
  publish({ element, isPinned })
}

export const scheduleHidePreview = () => {
  if (current.isPinned || !current.element) return
  cancelHidePreview()
  hideTimer = setTimeout(() => {
    hideTimer = null
    publish(NO_ANCHOR)
  }, PREVIEW_HIDE_DELAY_MS)
}

export const clearPreviewAnchor = () => {
  cancelHidePreview()
  if (current !== NO_ANCHOR) publish(NO_ANCHOR)
}

/**
 * Where the preview card hangs. Kept outside redux because it is a DOM node: the record and
 * its detail live in the `atlasPreview` slice, this only says which chip the card points at.
 */
export const usePreviewAnchor = (): IAtlasPreviewAnchor =>
  useSyncExternalStore(subscribe, getPreviewAnchor)
